import React, { useId } from 'react';

const INK = '#292332';
const WHITE = '#FFFEFA';
const HEART = '#ED527C';
const MOUTH = '#482735';

// Shapes are centered on each eye's existing position.
const EYE_SHAPES = {
  almond_01: 'M-24 0 Q0 -31 24 0 Q0 29 -24 0 Z',
  round_01: 'M-24 0 A24 24 0 1 0 24 0 A24 24 0 1 0 -24 0 Z',
  narrow_01: 'M-24 0 Q0 -17 24 0 Q0 18 -24 0 Z',
  hooded_01: 'M-23 -4 Q0 -16 23 -4 C23 29 -23 29 -23 -4 Z',
};

function EyeOpening({ shapeId, style, side }) {
  // Preserve the original oval eye geometry for older avatars.
  if (shapeId === 'oval_01') {
    if (style === 'angry') {
      return (
        <path
          d={
            side === 'left'
              ? 'M-22 -14 L22 -3 L22 4 C22 34 -22 34 -22 4 Z'
              : 'M-22 -3 L22 -14 L22 4 C22 34 -22 34 -22 4 Z'
          }
        />
      );
    }

    if (style === 'relaxed') {
      return (
        <path d="M-22 -1 H22 V4 C22 34 -22 34 -22 4 Z" />
      );
    }

    if (style === 'sleepy') {
      return (
        <path d="M-21 5 Q0 19 21 5 Q0 30 -21 5 Z" />
      );
    }

    return (
      <ellipse
        cx="0"
        cy="0"
        rx="22"
        ry={style === 'wide' ? 29 : 27}
      />
    );
  }

  return <path d={EYE_SHAPES[shapeId]} />;
}

export default function AvatarEyes({
  x = 0,
  side = 'left',
  style = 'open',
  isFemale = false,
  skin,
  eyeShape,
  eyeColor,
}) {
  // Each eye needs unique clip IDs, including thumbnail/export instances.
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const openingId = `avatar-eye-opening-${instanceId}`;
  const lidId = `avatar-eye-lid-${instanceId}`;

  const requestedShape = eyeShape?.id;
  const shapeId =
    requestedShape === 'oval_01' || EYE_SHAPES[requestedShape]
      ? requestedShape
      : 'oval_01';

  const isClassic =
    !eyeColor || eyeColor.id === 'eye_color_01';

  const irisColor = eyeColor?.color || INK;
  const shadow = skin?.shadow || '#A96940';

  const isOval = shapeId === 'oval_01';
  const isAngry = style === 'angry';
  const isRelaxed = style === 'relaxed';
  const isSleepy = style === 'sleepy';
  const isSad = style === 'sad';
  const isWide = style === 'wide';
  const hasLid = isAngry || isRelaxed || isSleepy;

  // Closed expressions temporarily hide the selected eye identity.
  if (style === 'closedHappy' || style === 'squeezed') {
    const isSqueezed = style === 'squeezed';

    const eyePath = isSqueezed
      ? side === 'left'
        ? 'M-19 -9 L2 2 L-19 12'
        : 'M19 -9 L-2 2 L19 12'
      : 'M-20 7 Q0 -16 20 7';

    return (
      <g transform={`translate(${x} 0)`}>
        <path
          d={eyePath}
          fill="none"
          stroke={INK}
          strokeWidth={isFemale ? 5 : 6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {isFemale && (
          <path
            d={
              side === 'left'
                ? isSqueezed
                  ? 'M-19 -9 L-26 -16'
                  : 'M-18 5 L-26 -2'
                : isSqueezed
                  ? 'M19 -9 L26 -16'
                  : 'M18 5 L26 -2'
            }
            fill="none"
            stroke={INK}
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}
      </g>
    );
  }

  // Heart eyes temporarily replace the entire eye.
  if (style === 'heart') {
    return (
      <g transform={`translate(${x} 0)`}>
        <path
          d="
            M0 23
            C-7 16 -25 3 -23 -10
            C-21 -24 -6 -26 0 -14
            C6 -26 21 -24 23 -10
            C25 3 7 16 0 23
            Z
          "
          fill={HEART}
          stroke={MOUTH}
          strokeWidth="2"
          strokeLinejoin="round"
        />

        <path
          d="M-16 -10 Q-13 -17 -8 -14"
          fill="none"
          stroke="#FFBDD0"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </g>
    );
  }

  // Stretch the chosen shape for surprise without replacing it.
  const shapeTransform =
    !isOval && isWide ? 'scale(1 1.2)' : undefined;

  // New shapes retain their outline while lids change the opening.
  const lidPath = isAngry
    ? side === 'left'
      ? 'M-30 -14 L30 1'
      : 'M-30 1 L30 -14'
    : isSleepy
      ? 'M-30 3 Q0 17 30 3'
      : 'M-30 -1 H30';

  const lidArea = `${lidPath} L40 45 H-40 Z`;

  const pupilX = isSleepy
    ? 0
    : style === 'sideLook'
      ? 9
      : side === 'left'
        ? 4
        : -4;

  const pupilY = isSleepy
    ? 16
    : isAngry || isRelaxed
      ? 12
      : isSad
        ? 7
        : 3;

  const irisWidth = isSleepy
    ? 6
    : isWide || isAngry || isRelaxed
      ? 9
      : 10;

  const irisHeight = isSleepy
    ? 5
    : isWide
      ? 11
      : isAngry || isRelaxed
        ? 10
        : 15;

  const showHighlight = !hasLid;

  const lashPath = isSleepy
    ? side === 'left'
      ? 'M-20 6 L-27 1'
      : 'M20 6 L27 1'
    : isAngry || isRelaxed
      ? side === 'left'
        ? 'M-21 -1 L-28 -7'
        : 'M21 -1 L28 -7'
      : side === 'left'
        ? 'M-19 -13 L-27 -20'
        : 'M19 -13 L27 -20';

  return (
    <g transform={`translate(${x} 0)`}>
      <defs>


        <clipPath
          id={openingId}
          clipPathUnits="userSpaceOnUse"
          transform={shapeTransform}
        >
          <EyeOpening
            shapeId={shapeId}
            style={style}
            side={side}
          />
        </clipPath>


        <clipPath id={lidId} clipPathUnits="userSpaceOnUse">
          {!isOval && hasLid ? (
            <path d={lidArea} />
          ) : (
            <rect x="-50" y="-50" width="100" height="100" />
          )}
        </clipPath>
      </defs>

      {/* Both clips contain the white, iris and pupil. */}
      <g clipPath={`url(#${openingId})`}>
        <g clipPath={`url(#${lidId})`}>
          <rect
            x="-40"
            y="-40"
            width="80"
            height="85"
            fill={WHITE}
          />

          <ellipse
            cx={pupilX}
            cy={pupilY}
            rx={irisWidth}
            ry={irisHeight}
            fill={isClassic ? INK : irisColor}
          />

          {!isClassic && (
            <ellipse
              cx={pupilX}
              cy={pupilY}
              rx={irisWidth * 0.46}
              ry={irisHeight * 0.7}
              fill={INK}
            />
          )}

          {showHighlight && (
            <circle
              cx={pupilX - 3}
              cy={pupilY - 6}
              r="3.5"
              fill="#FFFFFF"
            />
          )}
        </g>
      </g>

      {/* Preserve the original oval expression details. */}
      {isOval && hasLid && (
        <path
          d={
            isSleepy
              ? 'M-21 5 Q0 19 21 5'
              : isAngry
                ? side === 'left'
                  ? 'M-22 -14 L22 -3'
                  : 'M-22 -3 L22 -14'
                : 'M-22 -1 H22'
          }
          fill="none"
          stroke={INK}
          strokeWidth={isSleepy ? 4 : 3}
          strokeLinecap="round"
        />
      )}

      {!isOval && (
        <>
          {/* A thin outline makes the different shapes readable. */}
          <g
            transform={shapeTransform}
            fill="none"
            stroke={INK}
            strokeWidth="2.5"
            strokeLinejoin="round"
            clipPath={`url(#${lidId})`}
          >
            <EyeOpening
              shapeId={shapeId}
              style={style}
              side={side}
            />
          </g>

          {hasLid && (
            <g clipPath={`url(#${openingId})`}>
              <path
                d={lidPath}
                fill="none"
                stroke={INK}
                strokeWidth="3"
                strokeLinecap="round"
              />
            </g>
          )}

          {shapeId === 'hooded_01' && !hasLid && (
            <path
              d="M-20 -12 Q0 -24 20 -12"
              fill="none"
              stroke={shadow}
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}
        </>
      )}

      {isSad && (
        <path
          d="M-19 -14 Q0 -28 19 -14"
          fill="none"
          stroke={shadow}
          strokeWidth="5"
          strokeLinecap="round"
        />
      )}

      {isFemale && (
        <path
          d={lashPath}
          fill="none"
          stroke={INK}
          strokeWidth="4"
          strokeLinecap="round"
        />
      )}
    </g>
  );
}
